import { Modal, ModalBody, ModalFoot, ModalHead } from '@dixa/lib-shared-habla';
import { useForm } from 'react-hook-form';

import { HookFormText } from 'components/hookForm';
import { useNotifications } from 'hooks/useNotifications';

import { FormInfoBanner } from '../../../../components/formInfoBanner';
import { CustomForm } from '../../../../customForms.types';
import { useCustomForms } from '../../../../hooks/useCustomForms/useCustomForms';

import styles from './modals.module.scss';

type FormaTableProps = {
  form: CustomForm;
  onClose: () => void;
};

type FormValues = {
  title: string;
};

const maxNameLength = 256;

export function RenameFormModal({
  form,
  onClose,
}: FormaTableProps): JSX.Element {
  const customForms = useCustomForms();
  const notify = useNotifications();

  const { control, handleSubmit, reset, formState } = useForm<FormValues>({
    defaultValues: { title: form.title },
    shouldUnregister: true,
    mode: 'all',
  });

  const submitHandler = handleSubmit(async (values) => {
    const result = await customForms.renameForm(form, values.title);

    if (result.ok) {
      notify({
        title: `Form renamed to ${values.title}`,
        time: 5000,
        icon: 'done',
        className: 'success',
      });

      handleOnClose();
      return;
    }

    if (result.error) {
      notify({
        title: 'Could not rename the form',
        text: result.error,
        icon: 'exclamation',
        className: 'danger',
        time: 5000,
      });
    }
  });

  const handleOnClose = () => {
    reset();
    onClose();
  };

  return (
    <Modal className={styles.root} isOpen onClose={onClose}>
      <ModalHead title="Rename form" onClose={handleOnClose} />

      <form onSubmit={submitHandler}>
        <ModalBody>
          <FormInfoBanner />
          <HookFormText
            name="title"
            label="Name"
            control={control}
            disabled={formState.isSubmitting}
            rules={{
              required: true,
              maxLength: {
                value: maxNameLength,
                message: `Must not exceed ${maxNameLength} characters`,
              },
            }}
            componentProps={{
              placeholder: 'Messenger name',
              maxLength: maxNameLength,
              autoFocus: true,
            }}
          />
        </ModalBody>

        <ModalFoot
          cancelOnClick={handleOnClose}
          cancelProps={{ disabled: formState.isSubmitting }}
          confirmName="Rename form"
          confirmOnClick={submitHandler}
          confirmProps={{
            type: 'submit',
            isLoading: formState.isSubmitting,
            loadingIndicator: 'Renaming...',
          }}
        />
      </form>
    </Modal>
  );
}
